// BackfillBanner.tsx — Phase 2.F
//
// Top-of-app banner shown to admins of legacy practices (status='active',
// no signed BAA) prompting them to run the post-hoc HIPAA backfill.
//
// Mounted in AppShell alongside SandboxBanner. Self-hides for practices
// that don't need backfill (returns null).
//
// Design: amber/warning tone — this is a compliance gap that should be
// addressed soon, but the practice is still operational so we don't make
// it red/blocking.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, ArrowRight } from 'lucide-react';
import { backfillApi } from '../../lib/api';
import { useAuthStore } from '../../store';

interface BackfillRequiredResponse {
  required: boolean;
  reason: string;
  practice_status: string;
  has_signed_baa: boolean;
  in_progress: boolean;
}

export default function BackfillBanner() {
  const navigate = useNavigate();
  const { user, currentPractice } = useAuthStore();

  // Only admins see this banner (others can't act on it anyway).
  const roles = user?.roles ?? (user?.role ? [user.role] : []);
  const isAdmin = roles.includes('practice_admin') || roles.includes('platform_admin') || roles.includes('platform_operator');

  const requiredQuery = useQuery<BackfillRequiredResponse>({
    queryKey: ['backfill-required', currentPractice?.practice_id],
    queryFn: async () => {
      const r = await backfillApi.required();
      return r.data as BackfillRequiredResponse;
    },
    enabled: isAdmin && !!currentPractice,
    // The status doesn't change often; refetch on window focus + every 5 min.
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
    retry: false,
  });

  const data = requiredQuery.data;
  if (!data || (!data.required && !data.in_progress)) {
    return null;
  }

  // If a backfill is already in progress, the banner says "resume"
  const ctaLabel = data.in_progress ? 'Resume backfill' : 'Start HIPAA backfill';
  const headline = data.in_progress
    ? 'HIPAA backfill in progress'
    : 'Action required: complete HIPAA backfill';
  const description = data.in_progress
    ? 'You started the post-hoc HIPAA backfill but haven\'t finished. Resume to capture your remaining attestations and BAA.'
    : 'Your practice was onboarded before our HIPAA-aware wizard existed. Take ~15 minutes to capture missing designations + sign the standard BAA. No payment required — your practice is grandfathered.';

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-3 text-sm">
      <ShieldAlert className="w-4 h-4 text-amber-700 flex-shrink-0" />
      <div className="flex-1 leading-snug text-amber-900">
        <strong className="font-semibold uppercase tracking-wide text-xs mr-2">
          {data.in_progress ? 'Backfill in progress' : 'HIPAA gap'}
        </strong>
        <span className="font-medium">{headline}.</span>{' '}
        <span className="text-amber-800">{description}</span>
      </div>
      <button
        onClick={() => navigate('/backfill')}
        className="flex items-center gap-1 text-xs font-semibold underline hover:no-underline whitespace-nowrap text-amber-900"
      >
        {ctaLabel} <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
